import { Person, PhoneIphone } from '@mui/icons-material';
import {
    AlertColor,
    Box,
    Button,
    Drawer,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    Modal,
    TextField,
    Typography,
} from '@mui/material';
import { DelegatePermission } from 'api/models/User.type';
import confirmationAlert from 'components/Common/ConfirmAlert/ConfirmAlert';
import { MuiPhone } from 'components/Common/Phone/muiPhone';
import SectionHeader from 'components/Common/SectionHeader';
import TypeScriptTable from 'components/Common/Tables/TypeScriptTable/TypeScriptTable';
import useClientUsers from 'hooks/useClientUsers';
import useDebounce from 'hooks/useDebounce';
import {
  getMissingProfileFields,
  isProfileComplete,
} from 'hooks/useProfileComplete'
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom'
import { getUser, updateUser } from 'reducers/usersReducer';
import {
    getDelegateColumnsUser,
    getDelegatorColumnsUser,
} from './DelegateTables/DelegateTableColumns';
import UpdateUser from './UpdateUser/UpdateUser';
import UpdateUserDelegate from './UpdateUser/UpdateUserDelegate';

type UserClient = {
  company: String
}

type UserDetailProp = {
  company: UserClient
  userDetail: any
}

type AlertModalSpec = {
  message: AlertMessageSpec
  timeOut?: number
  closeButton?: boolean
}

type AlertMessageSpec = {
  header: string
  body: JSX.Element | string
}

const FIELD_ICONS: Record<string, JSX.Element> = {
  firstName: <Person />,
  lastName: <Person />,
  mobileNumber: <PhoneIphone />,
}

export const UserDetail = (props: UserDetailProp) => {
  const dispatch = useDispatch()
  const [openDrawer, setOpenDrawer] = React.useState(false)
  const [openDelegateDrawer, setOpenDelegateDrawer] = React.useState(false)
  const [alert, setAlert] = useState<AlertModalSpec>({
    message: { header: '', body: '' },
    timeOut: 5 * 60000,
  })
  const [alertType, setAlertType] = useState<AlertColor>()
  const [showAlert, setShowAlert] = useState(false)
  const location = useLocation()

  //get the client Users

  const { users: clientUsers } = props.userDetail.clientId
    ? useClientUsers(props.userDetail.clientId)
    : { users: [] }

  console.log('UserDetail props', props)

  useEffect(() => {
    console.log(`rerender`)
  }, [])

  const shouldBlockNavigation = () => !isProfileComplete(props.userDetail)

  useEffect(() => {
    const missingFields = getMissingProfileFields(props.userDetail)
    if (missingFields.length === 0) return

    setShowAlert(true)
    setAlert({
      message: {
        body: (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Typography>
              To be able to see and make bookings, we need to have up to date
              profile information.
            </Typography>
            <Typography>Please let us know the following:</Typography>
            <Typography component="div">
              <List disablePadding>
                {missingFields.map((field) => (
                  <ListItem key={field.key}>
                    <ListItemIcon>{FIELD_ICONS[field.key]}</ListItemIcon>
                    {field.label}
                  </ListItem>
                ))}
              </List>
            </Typography>
          </Box>
        ),
        header: 'Update your profile',
      },
      closeButton: true,
      timeOut: 5 * 60000,
    })
    setAlertType('error')
    setOpenDrawer(true)
  }, [location, props.userDetail])

  useEffect(() => {
    if (!shouldBlockNavigation()) setShowAlert(false)
    if (showAlert) {
      const timer = setTimeout(() => setShowAlert(false), alert.timeOut ?? 3000)
      return () => clearTimeout(timer)
    }
  }, [showAlert, shouldBlockNavigation])

  const deleteDelegateFromUser = async (delegateId: number) => {
    var existingDelegates = props.userDetail.delegates

    var removedDelegates = existingDelegates?.filter(
      (delegate: DelegatePermission) =>
        delegate.delegate.delegateId !== delegateId,
    )

    const updatedDelegates = removedDelegates.map((delegate: any) => ({
      permissionTypeName: delegate.permissionTypeName,
      delegateId: delegate.delegate.delegateId,
      startDateTime: delegate.delegate.startDateTime,
      endDateTime: delegate.delegate.endDateTime,
    }))

    confirmationAlert({
      title: 'Remove Delegate',
      action: async function (): Promise<void> {
        try {
          if (updatedDelegates) {
            await updateUser({
              updatedUser: {
                delegates: [...updatedDelegates],
              },
              userid: props.userDetail.userId,
            })
          } else {
            throw new Error('User delegate not found.')
          }
          setShowAlert(true)
          setAlertType('success')
          setAlert({
            message: { body: 'Delegate removed from user', header: 'Success' },
            timeOut: 5000,
          })
          dispatch(getUser())
        } catch (error) {
          setShowAlert(true)
          setAlertType('error')
          setAlert({
            message: {
              body: `No updates made: ${(error as any).message}`,
              header: 'Success',
            },
            timeOut: 5000,
          })

          console.error(
            `Error removing delegate with ID ${delegateId} from user ${props.userDetail.userId}:`,
            error,
          )
        }
        //refetch()
      },
      confirmationMessage: 'The action will remove the delegate from the user.',
      confirmationQuestion: 'Are you sure you want to proceed?',
    })
  }

  const handleAlertClose = () => setShowAlert(false)
  return (
    <>
      <Grid
        container
        style={{
          width: '90%',
          display: 'block',
          textAlign: 'left',
          paddingTop: '20px',
        }}
      >
        <Grid container sx={{ flexGrow: 1 }} alignItems="flex-start">
          <Grid item xs={6} style={{ textAlign: 'left' }}>
            <SectionHeader
              title="Profile"
              subtitle="Your profile information."
            />
          </Grid>
          <Grid
            item
            xs={6}
            style={{ textAlign: 'right' }}
            alignItems={'flex-start'}
          >
            <Button onClick={() => setOpenDrawer(true)} variant="outlined">
              Update Profile
            </Button>
          </Grid>
        </Grid>

        <Grid container sx={{ flexGrow: 1 }}>
          <Grid item xs={12}>
            <TextField
              sx={{ marginTop: '20px' }}
              label="Company"
              value={props.company}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              sx={{ marginTop: '20px' }}
              label="Email Address"
              value={props.userDetail.email}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              sx={{ marginTop: '20px' }}
              label="First Name"
              value={props.userDetail.firstName}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              sx={{ marginTop: '20px' }}
              label="Last Name"
              value={props.userDetail.lastName}
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <MuiPhone
              sx={{ marginTop: '20px' }}
              value={props.userDetail.contact?.mobileNumber || ''}
              label={'Mobile Number'}
              disabled
              onChange={() => {}}
            />
          </Grid>
          <Grid item xs={12}>
            <MuiPhone
              sx={{ marginTop: '20px' }}
              value={props.userDetail.contact?.officeNumber || ''}
              label={'Office Number'}
              disabled
              onChange={() => {}}
            />
          </Grid>
        </Grid>
        <Grid
          container
          sx={{ flexGrow: 1, marginTop: '40px' }}
          alignItems="flex-start"
        >
          <Grid item xs={8} style={{ textAlign: 'left' }}>
            <SectionHeader
              title="Delegate Access"
              subtitle="This section will tell you who you have access to book for and who you have provided access to view your bookings."
            />
          </Grid>
          <Grid
            item
            xs={6}
            style={{ textAlign: 'left' }}
            alignItems={'flex-start'}
          >
            <Button
              onClick={() => setOpenDelegateDrawer(true)}
              variant="outlined"
            >
              Add Delegate
            </Button>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '20px' }}>
            Your Delegates (Who can manage your bookings)
          </Grid>
          <Grid item xs={12}>
            <TypeScriptTable
              data={props.userDetail.delegates}
              columns={
                getDelegateColumnsUser(deleteDelegateFromUser).userTableColumns
              }
            />
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '50px' }}>
            Your Delegate Access (Who&apos;s bookings you can manage)
          </Grid>
          <Grid item xs={12}>
            <TypeScriptTable
              data={props.userDetail?.delegators || []}
              columns={getDelegatorColumnsUser().userTableColumns}
            />
          </Grid>
        </Grid>
      </Grid>

      <Modal
        open={showAlert}
        onClose={handleAlertClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography
            id="alert-message-header"
            variant="h6"
            component="h2"
            color={alertType}
          >
            {alert.message.header}
          </Typography>

          <Typography id="alert-message-body" sx={{ mt: 2 }}>
            {alert.message.body}
          </Typography>
          {alert.closeButton && (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end',
              }}
            >
              <Button onClick={handleAlertClose}>Ok</Button>
            </Box>
          )}
        </Box>
      </Modal>

      <Drawer
        anchor={'right'}
        open={useDebounce(openDrawer, 500)}
        onClose={() => {
          setOpenDrawer(false)
        }}
        PaperProps={{ sx: { width: '500px' } }}
      >
        <UpdateUser company={props.company} userDetail={props.userDetail} />
      </Drawer>
      <Drawer
        anchor={'right'}
        open={useDebounce(openDelegateDrawer, 500)}
        onClose={() => {
          setOpenDelegateDrawer(false)
        }}
        PaperProps={{ sx: { width: '500px' } }}
      >
        <UpdateUserDelegate
          userId={props.userDetail.userId}
          clientUsers={clientUsers}
        />
      </Drawer>
    </>
  )
}

export default UserDetail
